import { createFileRoute, Link, SearchSchemaInput, useNavigate } from '@tanstack/react-router'
import { useEffect, useMemo, useState } from 'react'
import { Info, Copy, ArrowUp, ArrowDown, ArrowUpDown, RefreshCw } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { useStore } from '@/store'
import { useShallow } from 'zustand/shallow'
import { toast } from 'sonner'
import { Api, copyGigSummaryToClipboard, getLspName } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import Select from '@/components/ui/select'
import Tooltip from '@/components/ui/tooltip'
import { DatePicker } from '@/components/ui/datepicker'
import { Pagination } from '@/components/ui/pagination'
import { MultiSelectFilter } from '@/components/ui/multi-select-filter'
import { orderGigTimeRange } from '@/components/features/orders/data-table'
import { OutletDetails } from '@/lib/interfaces'
import dayjs from 'dayjs'

// Radix rejects an empty string as a select item value, so "no filter" needs a sentinel that
// can't collide with a real status or store id.
const ANY = '__any__'

// Same status vocabulary/colors as the Slots page and the Orders page's (now-removed) gig
// summary bar.
const statusStyles: Record<string, string> = {
    Pending: 'bg-yellow-100 text-yellow-700',
    Processing: 'bg-blue-100 text-blue-700',
    Assigned: 'bg-indigo-100 text-indigo-700',
    'In-Progress': 'bg-purple-100 text-purple-700',
    Completed: 'bg-green-100 text-green-700',
    Cancelled: 'bg-red-100 text-red-700',
    Missed: 'bg-red-100 text-red-700',
}

// What /webui/gig/list actually returns: a bare array of raw gig_info rows (same convention as
// /webui/orders_detailed_v2 and /webui/slot/get_all - no {status, gigs: [...]} wrapper). Only
// the fields this page actually reads are typed here.
type GigApiRow = {
    gigId: string
    storeId: string
    pickupSlotStart?: string
    pickupSlotEnd?: string
    riderName?: string
    riderNumber?: string
    // The gig's workflow status (Pending/.../Completed/Cancelled/Missed) - not the same thing as
    // adherenceStatus, which tracks SLA compliance independently (e.g. a gig can be status
    // "Completed" yet adherenceStatus "Missed" if it finished late/under-time).
    status: string
    adherenceStatus?: string
    bppId?: string
    lspPayout?: number
    numOfOrders: number
    totalWorkingDuration?: number
    minServiceTimeMet?: number
}

// The shape this page renders against - `gigStatus` here is deliberately sourced from the API
// row's `status` field (see GigApiRow above), not `adherenceStatus`.
export type GigRow = {
    gigId: string
    storeId: string
    pickupSlotStart?: string
    pickupSlotEnd?: string
    riderName?: string
    riderNumber?: string
    gigStatus: string
    adherenceStatus?: string
    bppId?: string
    lspPayout?: number
    numOfOrders: number
    totalWorkingDuration?: number
    minServiceTimeMet?: number
}

export const Route = createFileRoute('/_auth/gigs/')({
    component: RouteComponent,
    validateSearch: (search: Record<string, unknown> & SearchSchemaInput): { date?: string } => {
        return search.date ? { date: search.date as string } : {}
    },
})

function RouteComponent() {
    const { token, setPage } = useStore(useShallow(state => ({
        token: state.token,
        setPage: state.setPage,
    })))

    useEffect(() => { setPage('Gigs') }, [])

    // Same URL-driven date as the Orders page: the picked date lives in the URL (?date=...), not
    // local state, so it survives a refresh/share/back-button, and the param is left off the URL
    // entirely when it's just today (cosmetic - the query itself always sends an explicit date).
    const navigate = useNavigate({ from: Route.fullPath })
    const today = dayjs().format('YYYY-MM-DD')
    const { date: dateSearchParam } = Route.useSearch()
    const date = dateSearchParam || today

    // Scopes by date the same way /webui/slot/get_all and /webui/orders_detailed_v2 already do -
    // returns a bare array (see GigApiRow above), not {status, gigs: [...]}.
    const gigsQuery = useQuery({
        queryKey: ['gigs', token, date],
        queryFn: () => Api<GigApiRow[]>('/webui/gig/list', 'POST', { date }, { token }),
        staleTime: 30 * 1000,
    })

    const getPickupList = useQuery({
        queryKey: ['pickupList', token],
        queryFn: () => Api<OutletDetails[]>('/webui/pickup/list', 'POST', {}, { token }),
        staleTime: Infinity,
        refetchOnWindowFocus: false,
    })
    const storeNameById = useMemo(
        () => new Map((getPickupList.data ?? []).map(store => [store.storeId, store.address?.name])),
        [getPickupList.data],
    )
    const storeName = (storeId: string) => storeNameById.get(storeId) || storeId

    const rows: GigRow[] = useMemo(
        () => (gigsQuery.data ?? []).map(g => ({
            gigId: g.gigId,
            storeId: g.storeId,
            pickupSlotStart: g.pickupSlotStart,
            pickupSlotEnd: g.pickupSlotEnd,
            riderName: g.riderName,
            riderNumber: g.riderNumber,
            gigStatus: g.status,
            adherenceStatus: g.adherenceStatus,
            bppId: g.bppId,
            lspPayout: g.lspPayout,
            numOfOrders: g.numOfOrders,
            totalWorkingDuration: g.totalWorkingDuration,
            minServiceTimeMet: g.minServiceTimeMet,
        })),
        [gigsQuery.data],
    )

    const [statusFilter, setStatusFilter] = useState(ANY)
    const [storeFilter, setStoreFilter] = useState<string[]>([])
    const [lspFilter, setLspFilter] = useState<string[]>([])
    const [timingFilter, setTimingFilter] = useState<string[]>([])
    // A busy account/day can plausibly run into thousands of gigs (the Slots page hit the same
    // wall before it got date-scoped) - paginate client-side rather than rendering every row.
    const [pageNum, setPageNum] = useState(1)
    const recordsPerPage = 100

    // Faceted, like Excel's filter dropdowns: each filter's own option list is built from rows
    // matching every *other* active filter (not itself) - so picking a store narrows the LSP/
    // Timing/Status lists down to what's actually happening at that store, instead of always
    // listing everything seen across the whole day regardless of what's already selected.
    const matchesStatus = (row: GigRow) => statusFilter === ANY || row.gigStatus === statusFilter
    const matchesStore = (row: GigRow) => storeFilter.length === 0 || storeFilter.includes(row.storeId)
    const matchesLsp = (row: GigRow) => lspFilter.length === 0 || (row.bppId != null && lspFilter.includes(getLspName(row.bppId)))
    const matchesTiming = (row: GigRow) => timingFilter.length === 0 || timingFilter.includes(orderGigTimeRange(row) || '')

    const statusOptions = useMemo(
        () => [...new Set(rows.filter(r => matchesStore(r) && matchesLsp(r) && matchesTiming(r)).map(row => row.gigStatus).filter(Boolean))].sort(),
        [rows, storeFilter, lspFilter, timingFilter],
    )
    const storeOptions = useMemo(
        () => [...new Set(rows.filter(r => matchesStatus(r) && matchesLsp(r) && matchesTiming(r)).map(row => row.storeId).filter(Boolean))]
            .sort((a, b) => storeName(a).localeCompare(storeName(b), undefined, { numeric: true }))
            .map(storeId => ({ label: storeName(storeId), value: storeId })),
        [rows, statusFilter, lspFilter, timingFilter, storeNameById],
    )
    const lspOptions = useMemo(
        () => [...new Set(rows.filter(r => matchesStatus(r) && matchesStore(r) && matchesTiming(r) && r.bppId).map(row => getLspName(row.bppId)))].sort(),
        [rows, statusFilter, storeFilter, timingFilter],
    )
    // The gig's pickup window as shown in the Timing column (e.g. "2pm - 4pm") - gigs are drawn
    // from a fixed rate card, so there's only ever a handful of distinct windows on any one day.
    // Ordered by the actual start instant, not alphabetically - "10pm - 11:59pm" sorting before
    // "2pm - 4pm" as text would make the list unreadable.
    const timingOptions = useMemo(() => {
        const startByLabel = new Map<string, number>()
        rows.filter(r => matchesStatus(r) && matchesStore(r) && matchesLsp(r)).forEach(row => {
            const label = orderGigTimeRange(row)
            if (!label) return
            const start = row.pickupSlotStart ? dayjs(row.pickupSlotStart).valueOf() : 0
            if (!startByLabel.has(label) || start < startByLabel.get(label)!) startByLabel.set(label, start)
        })
        return [...startByLabel.entries()].sort(([, a], [, b]) => a - b).map(([label]) => label)
    }, [rows, statusFilter, storeFilter, lspFilter])

    const filteredRows = useMemo(
        () => rows.filter(row => matchesStatus(row) && matchesStore(row) && matchesLsp(row) && matchesTiming(row)),
        [rows, statusFilter, storeFilter, lspFilter, timingFilter],
    )

    const isFiltered = statusFilter !== ANY || storeFilter.length > 0 || lspFilter.length > 0 || timingFilter.length > 0

    useEffect(() => { setPageNum(1) }, [statusFilter, storeFilter, lspFilter, timingFilter, date])

    const clearFilters = () => {
        setStatusFilter(ANY)
        setStoreFilter([])
        setLspFilter([])
        setTimingFilter([])
    }

    // Plain-table column sorting, same pattern as the Client Surge page (surge.tsx) - click a
    // column header to sort by it, click again to flip direction.
    type SortKey = 'gigId' | 'store' | 'timing' | 'rider' | 'lsp' | 'orders' | 'payout' | 'duration' | 'status' | 'adherenceStatus'
    const [sortKey, setSortKey] = useState<SortKey | null>(null)
    const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc')

    const sortValue = (row: GigRow, key: SortKey): string | number => {
        switch (key) {
            case 'gigId': return row.gigId
            case 'store': return storeName(row.storeId).toLowerCase()
            // Sorted by the actual instant, not the formatted "2pm - 4pm" string - lexical
            // sorting would put "10pm" before "2pm".
            case 'timing': return row.pickupSlotStart ? dayjs(row.pickupSlotStart).valueOf() : -Infinity
            case 'rider': return (row.riderName || row.riderNumber || '').toLowerCase()
            case 'lsp': return row.bppId ? getLspName(row.bppId).toLowerCase() : ''
            case 'orders': return row.numOfOrders
            case 'payout': return row.lspPayout ?? -Infinity
            case 'duration': return row.totalWorkingDuration ?? -Infinity
            case 'status': return row.gigStatus
            case 'adherenceStatus': return row.adherenceStatus || ''
        }
    }

    const toggleSort = (key: SortKey) => {
        if (sortKey !== key) { setSortKey(key); setSortDir('asc'); return }
        setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    }

    const SortIcon = ({ column }: { column: SortKey }) => {
        if (sortKey !== column) return <ArrowUpDown className="w-3 h-3 text-blue-200" />
        return sortDir === 'asc'
            ? <ArrowUp className="w-3 h-3 text-white" />
            : <ArrowDown className="w-3 h-3 text-white" />
    }

    const sortedRows = sortKey
        ? [...filteredRows].sort((a, b) => {
            const va = sortValue(a, sortKey)
            const vb = sortValue(b, sortKey)
            const cmp = va < vb ? -1 : va > vb ? 1 : 0
            return sortDir === 'asc' ? cmp : -cmp
        })
        : filteredRows

    // overflow-x-hidden here guarantees the page itself never scrolls sideways, no matter how
    // many columns the table grows to - the table's own overflow-x-auto wrapper below is what
    // actually lets you scroll to see them, contained to just that element.
    return (
        <div className="p-6 space-y-4 overflow-x-hidden">
            <div className="flex items-center justify-between">
                <h1 className="text-xl font-semibold">Gigs</h1>
                <Button loading={gigsQuery.isFetching} onClick={() => gigsQuery.refetch()}>
                    <RefreshCw className="w-4 h-4" /> Refresh
                </Button>
            </div>

            <div className="flex flex-wrap items-end gap-3">
                <div className="p-1">
                    <p className="mb-1 ml-1 text-sm font-medium text-gray-500">Date</p>
                    <DatePicker date={dayjs(date).toDate()} onChange={picked => {
                        const selected = dayjs(picked).format('YYYY-MM-DD')
                        navigate({
                            search: () => selected === today ? {} : { date: selected },
                        })
                    }} />
                </div>
                <div className="w-48">
                    <Select
                        label="Status"
                        options={[{ label: 'All statuses', value: ANY }, ...statusOptions.map(s => ({ label: s, value: s }))]}
                        value={statusFilter}
                        onChange={setStatusFilter}
                    />
                </div>
                <MultiSelectFilter
                    title="Store"
                    options={storeOptions}
                    selectedOptions={storeFilter}
                    onSelect={val => setStoreFilter(storeFilter.includes(val) ? storeFilter.filter(f => f !== val) : [...storeFilter, val])}
                    size="sm"
                    searchOptions
                />
                <MultiSelectFilter
                    title="LSP"
                    options={lspOptions}
                    selectedOptions={lspFilter}
                    onSelect={val => setLspFilter(lspFilter.includes(val) ? lspFilter.filter(f => f !== val) : [...lspFilter, val])}
                    size="sm"
                    searchOptions
                />
                <MultiSelectFilter
                    title="Timing"
                    options={timingOptions}
                    selectedOptions={timingFilter}
                    onSelect={val => setTimingFilter(timingFilter.includes(val) ? timingFilter.filter(f => f !== val) : [...timingFilter, val])}
                    size="sm"
                    searchOptions
                />
                {isFiltered && (
                    <div className="flex items-center gap-3 p-1 pb-2">
                        <button onClick={clearFilters} className="text-sm text-gray-500 underline hover:text-gray-700">
                            Clear
                        </button>
                        <span className="text-sm text-gray-500">
                            {filteredRows.length} of {rows.length}
                        </span>
                    </div>
                )}
            </div>

            {gigsQuery.isLoading && <p className="text-sm text-gray-500">Loading…</p>}
            {gigsQuery.isError && <p className="text-sm text-red-500">Failed to load gigs.</p>}

            {!gigsQuery.isLoading && rows.length === 0 && (
                <p className="text-sm text-gray-400">No gigs found.</p>
            )}

            {rows.length > 0 && filteredRows.length === 0 && (
                <p className="text-sm text-gray-400">No gigs match these filters.</p>
            )}

            {filteredRows.length > 0 && (
                <div className="overflow-x-auto rounded-lg border border-gray-200">
                    <table className="w-full text-sm">
                        <thead className="bg-blue-500 text-white sticky top-0">
                            <tr>
                                <th className="px-3 py-2 text-center">
                                    <button onClick={() => toggleSort('gigId')} className="flex items-center justify-center gap-1 mx-auto font-semibold hover:text-blue-100">
                                        Gig ID <SortIcon column="gigId" />
                                    </button>
                                </th>
                                <th className="px-3 py-2 text-center">
                                    <button onClick={() => toggleSort('store')} className="flex items-center justify-center gap-1 mx-auto font-semibold hover:text-blue-100">
                                        Store <SortIcon column="store" />
                                    </button>
                                </th>
                                <th className="px-3 py-2 text-center">
                                    <button onClick={() => toggleSort('timing')} className="flex items-center justify-center gap-1 mx-auto font-semibold hover:text-blue-100">
                                        Timing <SortIcon column="timing" />
                                    </button>
                                </th>
                                <th className="px-3 py-2 text-center">
                                    <button onClick={() => toggleSort('rider')} className="flex items-center justify-center gap-1 mx-auto font-semibold hover:text-blue-100">
                                        Rider <SortIcon column="rider" />
                                    </button>
                                </th>
                                <th className="px-3 py-2 text-center">
                                    <button onClick={() => toggleSort('lsp')} className="flex items-center justify-center gap-1 mx-auto font-semibold hover:text-blue-100">
                                        LSP <SortIcon column="lsp" />
                                    </button>
                                </th>
                                <th className="px-3 py-2 text-center">
                                    <button onClick={() => toggleSort('orders')} className="flex items-center justify-center gap-1 mx-auto font-semibold hover:text-blue-100">
                                        Orders <SortIcon column="orders" />
                                    </button>
                                </th>
                                <th className="px-3 py-2 text-center">
                                    <button onClick={() => toggleSort('payout')} className="flex items-center justify-center gap-1 mx-auto font-semibold hover:text-blue-100">
                                        LSP Payout <SortIcon column="payout" />
                                    </button>
                                </th>
                                <th className="px-3 py-2 text-center">
                                    <button onClick={() => toggleSort('duration')} className="flex items-center justify-center gap-1 mx-auto font-semibold hover:text-blue-100">
                                        Working Duration <SortIcon column="duration" />
                                    </button>
                                </th>
                                <th className="px-3 py-2 text-center">
                                    <button onClick={() => toggleSort('status')} className="flex items-center justify-center gap-1 mx-auto font-semibold hover:text-blue-100">
                                        Status <SortIcon column="status" />
                                    </button>
                                </th>
                                <th className="px-3 py-2 text-center">
                                    <button onClick={() => toggleSort('adherenceStatus')} className="flex items-center justify-center gap-1 mx-auto font-semibold hover:text-blue-100">
                                        Adherence Status <SortIcon column="adherenceStatus" />
                                    </button>
                                </th>
                                <th className="px-3 py-2 text-center font-semibold">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {sortedRows.slice((pageNum - 1) * recordsPerPage, pageNum * recordsPerPage).map(row => (
                                <tr key={row.gigId} className="hover:bg-gray-50 text-center">
                                    <td className="px-3 py-2">{row.gigId}</td>
                                    <td className="px-3 py-2">{storeName(row.storeId)}</td>
                                    <td className="px-3 py-2">{orderGigTimeRange(row) || '—'}</td>
                                    <td className={`px-3 py-2 ${row.riderName || row.riderNumber ? '' : 'text-gray-400 italic'}`}>
                                        {row.riderName || row.riderNumber || 'Rider not assigned'}
                                    </td>
                                    <td className="px-3 py-2">{row.bppId ? getLspName(row.bppId) : '—'}</td>
                                    <td className="px-3 py-2">{row.numOfOrders}</td>
                                    <td className="px-3 py-2">{row.lspPayout != null ? `₹${row.lspPayout}` : '—'}</td>
                                    <td className="px-3 py-2">{row.totalWorkingDuration != null ? `${row.totalWorkingDuration} min` : '—'}</td>
                                    <td className="px-3 py-2">
                                        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${statusStyles[row.gigStatus] ?? 'bg-gray-100 text-gray-600'}`}>
                                            {row.gigStatus}
                                        </span>
                                    </td>
                                    <td className="px-3 py-2">
                                        {row.adherenceStatus ? (
                                            <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${statusStyles[row.adherenceStatus] ?? 'bg-gray-100 text-gray-600'}`}>
                                                {row.adherenceStatus}
                                            </span>
                                        ) : '—'}
                                    </td>
                                    <td className="px-3 py-2">
                                        <div className="flex items-center justify-center gap-1">
                                            <Tooltip title="Copy gig details">
                                                <button
                                                    type="button"
                                                    aria-label={`Copy gig ${row.gigId}`}
                                                    className="p-1 rounded hover:bg-gray-200 cursor-pointer"
                                                    onClick={async () => {
                                                        const didCopy = await copyGigSummaryToClipboard({
                                                            gigId: row.gigId,
                                                            storeName: storeName(row.storeId),
                                                            gigTime: orderGigTimeRange(row),
                                                            riderName: row.riderName || row.riderNumber,
                                                            lspName: row.bppId ? getLspName(row.bppId) : undefined,
                                                            gigStatus: row.gigStatus,
                                                            lspPayout: row.lspPayout,
                                                            numOfOrders: row.numOfOrders,
                                                            totalWorkingDuration: row.totalWorkingDuration,
                                                            minServiceTimeMet: row.minServiceTimeMet,
                                                        })
                                                        if (didCopy) {
                                                            toast.success('Gig details copied to clipboard')
                                                        } else {
                                                            toast.error('Failed to copy gig details')
                                                        }
                                                    }}
                                                >
                                                    <Copy className="w-4 h-4" />
                                                </button>
                                            </Tooltip>
                                            <Tooltip title="View gig details">
                                                <Link
                                                    to="/gigs/$gigId"
                                                    params={{ gigId: row.gigId }}
                                                    aria-label={`View gig ${row.gigId}`}
                                                    className="inline-flex p-1 rounded hover:bg-gray-200"
                                                >
                                                    <Info className="w-4 h-4" />
                                                </Link>
                                            </Tooltip>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {filteredRows.length > recordsPerPage && (
                <Pagination recordCount={filteredRows.length} page={pageNum} setPage={setPageNum} recordsPerPage={recordsPerPage} label="gigs" inline />
            )}
        </div>
    )
}
